/**
 * The selection action bar — `KLQ-20260825-006` §G.
 *
 * **Why a second door at all.** Right-click is fast, undiscoverable, and absent on a touch
 * screen. A product whose only way to act on twelve rows is a gesture nobody told you about
 * has, for most of its users, no bulk actions. So the same constructed list renders twice: in
 * the context menu, and here.
 *
 * **One list, two renderers.** This component takes `MenuItem`s — the identical objects the
 * menu draws — and decides only *where* each one goes: the enabled leaves become buttons up
 * to a small ceiling, and everything else (the submenus, the ones disabled with a reason, the
 * overflow) goes into the `⋯` beside them. An action added to a constructor therefore appears
 * in both doors without being mentioned twice, which is the drift retro rule 12 was written
 * about, made structurally impossible rather than merely discouraged.
 *
 * **It also carries the two beats a bulk action needs**, because they belong next to the
 * count and not in a dialog over the table:
 *
 *   - the **question** (§H.3) — *"Assign 12 tasks to Zuzana?"* — asked before anything is
 *     written whenever more than one row is about to change. It is the same shape the cell
 *     editor already uses for a large fan-out: the sentence appears where the person is
 *     looking, not in a modal that has to be dismissed before they can see what it is about.
 *   - the **result** — *"12 assigned, 2 skipped — closed."* The spec calls this a toast, and
 *     there is no toast mechanism in this codebase; inventing one here would be a second
 *     notification system owned by a spec that is not about notifications. It lands in this
 *     bar's live region instead, which is also §I.3's one polite announcement of the count.
 *
 * **No copy of its own.** Every word arrives as a prop, the way `Menu`, `ChipSelect` and
 * `Slicer` take theirs.
 */

import { X } from "lucide-react";

import { Menu } from "./Menu";
import { drawnActions, isSeparator } from "./MenuPopup";
import type { MenuItem } from "./MenuPopup";
import styles from "./SelectionBar.module.css";

/** A question asked before a write that reaches more than one row (§H.3). */
export interface SelectionBarConfirm {
  /** The whole sentence, count included, already translated. */
  readonly question: string;
  readonly apply: string;
  readonly cancel: string;
  readonly onApply: () => void;
  readonly onCancel: () => void;
}

export interface SelectionBarProps {
  /** *"12 tasks selected"* — the subject count in a sentence, already translated (§D.2). */
  readonly summary: string;
  readonly items: readonly MenuItem[];
  readonly onClear: () => void;
  readonly clearLabel: string;
  /** The `⋯` trigger's accessible name. */
  readonly moreLabel: string;
  /** What just happened, in one sentence. Cleared by the surface that set it. */
  readonly status?: string | null | undefined;
  readonly confirm?: SelectionBarConfirm | null | undefined;
}

/**
 * How many actions get to be buttons. Three, because a bar is a place to look and the fourth
 * button is where a row of actions stops being read and starts being scanned past — the same
 * finding that produced `Menu` in the first place.
 */
const BUTTONS = 3;

export function SelectionBar({
  summary,
  items,
  onClear,
  clearLabel,
  moreLabel,
  status,
  confirm,
}: SelectionBarProps) {
  const actions = drawnActions(items);
  // A submenu cannot be a button — a button that opens a list is a menu with extra steps —
  // and a disabled action keeps its reason, which only the menu has room to show.
  const promoted = actions
    .filter((one) => one.items === undefined && one.disabled !== true)
    .slice(0, BUTTONS);
  const promotedIds = new Set(promoted.map((one) => one.id));
  const rest = trimmed(items, promotedIds);

  return (
    <div className={styles.bar} role="group" aria-label={summary}>
      <span className={styles.summary} role="status">
        {status == null || status === "" ? summary : status}
      </span>

      {confirm == null ? (
        <>
          <div className={styles.actions}>
            {promoted.map((action) => (
              <button
                key={action.id}
                type="button"
                className={[
                  styles.action,
                  action.tone === "danger" ? styles.danger : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => {
                  action.onSelect?.();
                }}
              >
                {action.icon}
                <span>{action.label}</span>
              </button>
            ))}
            {rest.length === 0 ? null : (
              <Menu label={moreLabel} items={rest} align="end" />
            )}
          </div>

          <button
            type="button"
            className={styles.clear}
            aria-label={clearLabel}
            onClick={onClear}
          >
            <X size={14} strokeWidth={1.5} aria-hidden />
          </button>
        </>
      ) : (
        <div className={styles.confirm}>
          {/* **Announced when it appears.** The question replaces the bar's own `role="status"`
              region, so without a live role of its own a keyboard or screen-reader user gets
              silence where a mouse user gets a question — and the menu that raised it has
              already handed focus back to the grid (`MenuCloseReason.chosen`). `alert` rather
              than `status` because this one is blocking: nothing happens until it is answered.
              Which control should *take* focus here is a separate call and is still open —
              see `KLQ-20260825-006` §I's note. */}
          <span className={styles.question} role="alert">
            {confirm.question}
          </span>
          <button type="button" className={styles.action} onClick={confirm.onApply}>
            {confirm.apply}
          </button>
          <button type="button" className={styles.action} onClick={confirm.onCancel}>
            {confirm.cancel}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * The list the `⋯` gets: everything the bar did not promote. The stranded rules are not
 * pruned here — `MenuPopup` already does that for every list it draws, and doing it twice is
 * how the two copies eventually disagree about what a stranded rule is.
 */
function trimmed(
  items: readonly MenuItem[],
  promoted: ReadonlySet<string>,
): MenuItem[] {
  return items.filter((item) => isSeparator(item) || !promoted.has(item.id));
}
